import React from "react";
import styled from 'styled-components';

import { LabeledDataRow } from "./LabeledDataRow";
import { ContributorRow } from "./ContributorRow";
import { useContributorsByRole } from "../hooks/useContributorsByRole";
import productsData from "../data/products.json";

const ContentContainer = styled.div`
	box-sizing: border-box;
	padding: 1.75rem 1.5rem;
	max-width: 32rem;
	margin: 0 auto;

	>:first-child {
		margin-top: 0;
	}

	${LabeledDataRow} {
		margin-top: 2rem;
	}

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.third}) {
		margin: 0;
		padding: 2rem;
		width: 64rem;
		max-width: 64rem;
`;

const StyledInfoBlock = styled.section`
	background: ${(props) => props.theme.colors.neutral10};
	color: ${(props) => props.theme.colors.neutral2};

	@media only screen and (orientation: landscape) {
		display: flex;
		flex-direction: row;
		justify-content: center;
	}
`;

const Title = styled.h3`
	${(props) => props.theme.typography.headingL};
	margin-top: 0;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.third}) {
		${(props) => props.theme.typography.headingXL};
	}
`;

const AdditionalInfo = styled.div`
	margin-top: 2rem;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.third}) {
		display: grid;
		gap: 4rem;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		margin-top: 4rem;
	}
`;

const InfoSection = styled.div`
	margin-top: 2rem;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.third}) {
		margin-top: 0;
	}
`;

const InfoTitle = styled.h4`
	${(props) => props.theme.typography.headingS};
	margin: 0 0 1rem;
`;

const HistoryList = styled.dl`
	display: grid;
	gap: 1.5rem 1.25rem;
	grid-template-columns: auto 1fr;
	margin: 0;
`;

const Version = styled.dt`
	${(props) => props.theme.typography.bodyXS};
	font-weight: bold;
	color: ${(props) => props.theme.colors.yellow4};
	margin: 0;
	text-align: right;
`;

const ReleaseDate = styled.span`
	${(props) => props.theme.typography.body2XS};
	color: ${(props) => props.theme.colors.neutral4};
	display: block;
	font-weight: normal;
`;

const VersionDescription = styled.dd`
	${(props) => props.theme.typography.body2XS};
	margin: 0.2rem 0 0;
`;

const CompatibilityTable = styled.table`
	border-collapse: collapse;
	width: 100%;

	th,
	td {
		padding: 0.6rem 0.75rem;
		text-align: center;
	}

	th:first-child,
	td:first-child {
		padding-left: 0;
		text-align: left;
	}

	th {
		${(props) => props.theme.typography.headingXS};
	}

	td {
		${(props) => props.theme.typography.bodyXS};
	}
`;

const Compatible = styled.span`
	color: ${(props) => props.theme.colors.teal4};
`;

const CompatibilityNote = styled.p`
	${(props) => props.theme.typography.body2XS};
	color: ${(props) => props.theme.colors.neutral4};
	margin: 0.75rem 0 0;
`;

export function InfoBlock({ productId }) {
	const contributorData = useContributorsByRole(productId);
	const product = productsData.find(({ id }) => id === productId);
	
	if (!contributorData) {
		return null;
	}
	
	const { contributorsByRole, roleLabels, roleOrder } = contributorData;
	const versionHistory = product?.versionHistory || [];
	const compatibility = product?.compatibility || { versions: [], matrix: {} };
	const compatibilityVersions = compatibility.versions;
	
	return (
		<StyledInfoBlock>
			<ContentContainer>
				<Title>Tressym {product?.name}</Title>
				{roleOrder.map(role => {
					if (!contributorsByRole[role]) return null;
					
					return (
						<ContributorRow 
							key={role}
							roleLabel={roleLabels[role]}
							contributorIds={contributorsByRole[role]}
						/>
					);
				})}
				<AdditionalInfo>
					<InfoSection>
						<InfoTitle>Version history</InfoTitle>
						<HistoryList>
							{versionHistory.map(({ versionNumber, releaseDate, description }) => (
								<React.Fragment key={versionNumber}>
									<Version>
										{versionNumber}
										<ReleaseDate>{releaseDate}</ReleaseDate>
									</Version>
									<VersionDescription>{description}</VersionDescription>
								</React.Fragment>
							))}
						</HistoryList>
					</InfoSection>
					<InfoSection>
						<InfoTitle>Compatibility</InfoTitle>
						<CompatibilityTable>
							<thead>
								<tr>
									<th scope="col"></th>
									{compatibilityVersions.map(version => <th key={version} scope="col">{version}</th>)}
								</tr>
							</thead>
							<tbody>
								{compatibilityVersions.map(rowVersion => (
									<tr key={rowVersion}>
										<th scope="row">{rowVersion}</th>
										{compatibilityVersions.map(columnVersion => (
											<td key={columnVersion}>
												{rowVersion !== columnVersion && (
													<Compatible>{compatibility.matrix[rowVersion][columnVersion]}</Compatible>
												)}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</CompatibilityTable>
						<CompatibilityNote>*When switching arms, switch both left and right arms at the same time.</CompatibilityNote>
					</InfoSection>
				</AdditionalInfo>
			</ContentContainer>
		</StyledInfoBlock>
	);
}